import { P2Accordion, P2TextDescription, P2TextLabel } from "react-bootstrap-common-script";
import { Separator } from "../../separator";
import AccordionTitleAmoount from "../../accordion/accordion-title-amount/accordion-title-amount";
import { Helpers } from "../../../utils/helper";
import { useMemo } from "react";
import RadioGroup from "../../form/grouped/radio/radio-group";
import { RadioItemInterface } from "../../../models/interfaces/radio-item.interface";
import { useFinancialPlanContext } from "../../../context/financial-plan-contenxt";
import { updateArrayState, updateSingleNullableState, updateSingleState } from "../../../utils/financial-plan";

const CASH_OPTION: RadioItemInterface = {
  id: 999,
  description: `Payable by Cash`,
  value: "cash",
  variant: "default",
};

const AdjustFeePayable = () => {
  // State
  const {
    constant: { owner_details },
    stampFee,
    conveyancingFee,
    registrationFee,
    hpsFees,
    extraFees,
    setStampFee,
    setConveyancingFee,
    setRegistrationFee,
    setHpsFees,
    setExtraFees,
    derivedValue: { totalFee },
  } = useFinancialPlanContext();

  const allUserRadioItems = useMemo(() => {
    const usersRadios = owner_details.map((owner_detail, _index) => {
      let radioItem: RadioItemInterface = {
        id: _index,
        description: `Payable by CPF by ${owner_detail.name}`,
        value: owner_detail.uinfin,
        variant: "default",
      };

      return radioItem;
    });

    return [...usersRadios, CASH_OPTION];
  }, [owner_details]);

  return (
    <div>
      <P2TextDescription color="#333" title={"Fees Payable"} />
      <div className="mt-3">
        <P2TextLabel color="#333" fontSize={"1.125rem"} bold={400} label={"You can pick your payment options for the fees listed below."} />
      </div>

      <div style={{ paddingTop: "32px", display: "flex", flexDirection: "column", gap: 32 }}>
        <P2Accordion
          defaultOpen={true}
          title={<AccordionTitleAmoount title="Fees Payable" description={Helpers.formatThousandSeparator(totalFee.toFixed(2), "$")} />}
        >
          <Separator />
          <div style={{ paddingTop: "32px", display: "flex", flexDirection: "column", gap: 32 }}>
            {stampFee ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                  <P2TextLabel fontSize={"1.125rem"} bold={700} label={"Stamp fees"} />
                  <P2TextLabel
                    fontSize={"1.125rem"}
                    bold={700}
                    label={`${Helpers.formatThousandSeparator((stampFee.basic_amount + stampFee.additional_amount).toFixed(2), "$")}`}
                  />
                </div>
                <RadioGroup
                  required
                  layout="default"
                  hasTooltip={true}
                  tooltipContent={`This situation can apply to any current owner.`}
                  items={allUserRadioItems}
                  value={stampFee.paid_by}
                  onItemSelect={(value) => updateSingleNullableState(setStampFee, "paid_by", value)}
                />
              </div>
            ) : null}

            {conveyancingFee ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                  <P2TextLabel fontSize={"1.125rem"} bold={700} label={"Conveyancing fees"} />{" "}
                  <P2TextLabel
                    fontSize={"1.125rem"}
                    bold={700}
                    label={`${Helpers.formatThousandSeparator((conveyancingFee.basic_amount + conveyancingFee.additional_amount).toFixed(2), "$")}`}
                  />
                </div>
                <RadioGroup
                  required
                  layout="default"
                  hasTooltip={true}
                  tooltipContent={`This situation can apply to any current owner.`}
                  items={allUserRadioItems}
                  value={conveyancingFee.paid_by}
                  onItemSelect={(value) => updateSingleNullableState(setConveyancingFee, "paid_by", value)}
                />
              </div>
            ) : null}

            {registrationFee ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                  <P2TextLabel fontSize={"1.125rem"} bold={700} label={"Registration fees"} />
                  <P2TextLabel
                    fontSize={"1.125rem"}
                    bold={700}
                    label={`${Helpers.formatThousandSeparator((registrationFee.basic_amount + registrationFee.additional_amount).toFixed(2), "$")}`}
                  />
                </div>
                <RadioGroup
                  required
                  layout="default"
                  hasTooltip={true}
                  tooltipContent={`This situation can apply to any current owner.`}
                  items={allUserRadioItems}
                  value={registrationFee.paid_by}
                  onItemSelect={(value) => updateSingleNullableState(setRegistrationFee, "paid_by", value)}
                />
              </div>
            ) : null}

            {hpsFees
              ? hpsFees.map((hpsFee) => {
                  const ownerDetail = owner_details.find((owner_detail) => owner_detail.uinfin === hpsFee.uinfin);

                  if (!ownerDetail) return null;

                  const radioSelection: RadioItemInterface[] = [
                    {
                      id: 1,
                      description: `Payable by CPF by ${ownerDetail.name}`,
                      value: ownerDetail.uinfin,
                      variant: "default",
                    },
                    CASH_OPTION,
                  ];

                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                        <P2TextLabel fontSize={"1.125rem"} bold={700} label={`Home Protection Scheme (${ownerDetail.name})`} />
                        <P2TextLabel
                          fontSize={"1.125rem"}
                          bold={700}
                          label={`${Helpers.formatThousandSeparator((hpsFee.basic_amount + hpsFee.additional_amount).toFixed(2), "$")}`}
                        />
                      </div>
                      <RadioGroup
                        required
                        layout="default"
                        hasTooltip={true}
                        tooltipContent={`This situation can apply to any current owner.`}
                        items={radioSelection}
                        value={hpsFee.paid_by}
                        onItemSelect={(value) => updateArrayState(setHpsFees, "paid_by", value, "uinfin", hpsFee.uinfin)}
                      />
                    </div>
                  );
                })
              : null}

            {extraFees
              ? extraFees.map((extraFee) => (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                      <P2TextLabel fontSize={"1.125rem"} bold={700} label={extraFee.name_of_fee} />
                      <P2TextLabel
                        fontSize={"1.125rem"}
                        bold={700}
                        label={`${Helpers.formatThousandSeparator((extraFee.basic_amount + extraFee.additional_amount).toFixed(2), "$")}`}
                      />
                    </div>
                    <RadioGroup
                      required
                      layout="default"
                      hasTooltip={true}
                      tooltipContent={`This situation can apply to any current owner.`}
                      items={allUserRadioItems}
                      value={extraFee.paid_by}
                      onItemSelect={(value) => updateArrayState(setExtraFees, "paid_by", value, "name_of_fee", extraFee.name_of_fee)}
                    />
                  </div>
                ))
              : null}
          </div>
        </P2Accordion>
      </div>
    </div>
  );
};

export default AdjustFeePayable;
